'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import { useNamespacedTranslate } from '@/shared/lib/locales';

export type ModalVariant = 'default' | 'danger';

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ModalVariant;
}

export interface AlertOptions {
  title?: string;
  message: string;
  buttonLabel?: string;
  variant?: ModalVariant;
}

type PendingModal =
  | { kind: 'confirm'; options: ConfirmOptions; resolve: (value: boolean) => void }
  | { kind: 'alert'; options: AlertOptions; resolve: (value: void) => void };

interface ModalContextValue {
  confirm: (input: string | ConfirmOptions) => Promise<boolean>;
  alert: (input: string | AlertOptions) => Promise<void>;
}

const ModalContext = createContext<ModalContextValue | null>(null);

// Substitui window.confirm/window.alert por um modal consistente com o resto da UI.
export function useModal(): ModalContextValue {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal deve ser usado dentro de um ModalProvider');
  }
  return context;
}

export function ModalProvider({ children }: { children: React.ReactNode }) {
  const t = useNamespacedTranslate('modal');
  const [pending, setPending] = useState<PendingModal | null>(null);

  const confirm = (input: string | ConfirmOptions): Promise<boolean> => {
    const options = typeof input === 'string' ? { message: input } : input;
    return new Promise<boolean>((resolve) => {
      setPending({ kind: 'confirm', options, resolve });
    });
  };

  const alertFn = (input: string | AlertOptions): Promise<void> => {
    const options = typeof input === 'string' ? { message: input } : input;
    return new Promise<void>((resolve) => {
      setPending({ kind: 'alert', options, resolve });
    });
  };

  const handleClose = (confirmed: boolean) => {
    setPending((current) => {
      if (!current) return null;
      if (current.kind === 'confirm') current.resolve(confirmed);
      else current.resolve();
      return null;
    });
  };

  useEffect(() => {
    if (!pending) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose(false);
      if (e.key === 'Enter') handleClose(true);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [pending]);

  const isDanger = pending?.options.variant === 'danger';

  return (
    <ModalContext.Provider value={{ confirm, alert: alertFn }}>
      {children}
      {pending && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6 space-y-5">
            <div className="flex items-start gap-3">
              <span
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                  isDanger ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {isDanger ? <AlertTriangle className="h-5 w-5" /> : <Info className="h-5 w-5" />}
              </span>
              <div className="min-w-0 pt-1">
                {pending.options.title && (
                  <h2 className="text-base font-semibold text-gray-900">{pending.options.title}</h2>
                )}
                <p className="text-sm text-gray-600 mt-0.5 whitespace-pre-line">{pending.options.message}</p>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              {pending.kind === 'confirm' && (
                <button
                  onClick={() => handleClose(false)}
                  className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  {pending.options.cancelLabel ?? t('cancelButton')}
                </button>
              )}
              <button
                autoFocus
                onClick={() => handleClose(true)}
                className={`px-4 py-2 text-sm rounded-lg text-white transition-colors ${
                  isDanger ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-900 hover:bg-gray-700'
                }`}
              >
                {pending.kind === 'confirm'
                  ? (pending.options.confirmLabel ?? t('confirmButton'))
                  : (pending.options.buttonLabel ?? t('okButton'))}
              </button>
            </div>
          </div>
        </div>
      )}
    </ModalContext.Provider>
  );
}
